import { Button, Card, CardContent } from '@design-system';
import { durationInWords } from '@shared/consumerPlan';
import { DetailList, Figure, PaymentSplitBar } from '../components/figures';
import { SelectedCoverageSummary } from '../components/selected-coverage-summary';
import { StepFrame } from '../components/step-frame';
import { useCareWalkthrough } from '../hooks/use-care-walkthrough';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { GUARANTEE_CONDITIONS } from '../lib/copy';
import { approxMoney, money } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

export function WalkthroughSummaryStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { summary, selectedQuote, selectedProjection, outOfPocket } = useCareWalkthrough(clientId);
  if (!summary || !selectedQuote) return null;

  const guaranteedDeathBenefit = selectedProjection?.guaranteedDeathBenefitAfterCare ?? 0;

  return (
    <StepFrame
      step="walkthrough-summary"
      title="How does the whole event come together?"
      subtitle="Your scenario, from the first care bill to the last."
      onBack={() => back('coverage')}
      help={
        <>
          <p>“Bills without funds when they arrive” checks timing month by month. Insurance arriving later can’t pay an earlier bill on time.</p>
          <p>It can be larger than the overall gap. You may need cash to bridge a waiting period even when total future benefits look sufficient.</p>
          <div className="flex flex-col items-start">
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('walkthrough')}>
              Walk through it month by month
            </Button>
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('review')}>
              Review my whole plan
            </Button>
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('breakdown', { returnTo: 'walkthrough-summary' })}>
              See the care cost breakdown
            </Button>
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('no-care', { returnTo: 'walkthrough-summary' })}>
              What if I never need care?
            </Button>
          </div>
        </>
      }
      primary={{ label: 'Apply for this policy', onClick: () => goTo('application-start') }}
      secondary={{
        label: 'I want to self-fund the entire event',
        onClick: () => goTo('self-fund', { returnTo: 'walkthrough-summary' }),
      }}
      next="start your application. We’ll prefill what we can."
    >
      <SelectedCoverageSummary quote={selectedQuote} />
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Figure label="Care duration" value={durationInWords(summary.months)} />
            <Figure label="Total care cost" value={approxMoney(summary.totalCareCost)} />
            <Figure label="Projected policy payout" value={approxMoney(summary.paidByInsurance)} tone="positive" />
            <Figure
              label="Left for you to cover"
              value={approxMoney(outOfPocket)}
              hint="What insurance isn’t expected to pay. You can cover it from savings, income or family help."
            />
          </div>
          <PaymentSplitBar
            total={summary.totalCareCost}
            insurance={summary.paidByInsurance}
            label={`Insurance is projected to pay about ${Math.round((summary.paidByInsurance / Math.max(1, summary.totalCareCost)) * 100)}% of the care cost`}
          />
        </CardContent>
      </Card>
      <DetailList
        rows={[
          { label: 'Bills without funds when they arrive', value: approxMoney(summary.stillNeeded) },
          ...(guaranteedDeathBenefit > 0
            ? [{ label: 'Still guaranteed for your beneficiaries', value: `at least ${money(guaranteedDeathBenefit)}` }]
            : []),
        ]}
      />
      {guaranteedDeathBenefit > 0 && (
        <p className="text-muted-foreground text-xs">{GUARANTEE_CONDITIONS}</p>
      )}
    </StepFrame>
  );
}
